import React from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeftCircle, PauseCircle, LogOut } from 'lucide-react';
import { Modal } from './Modal';
import { useTour } from '../src/contexts/TourContext';

/**
 * Reemplaza el cierre silencioso de TourContext.tsx (antes: navegar fuera del tour lo cerraba
 * sin ningún aviso, se sentía como un bug) -- is shown cuando exitPromptPending es true.
 * z-index por encima del spotlight del tour (backdrop z-[1000] default de Modal.tsx queda por
 * DEBAJO del hole/tooltip del spotlight, z-1001/1002 -- este modal necesita quedar por encima
 * de ambos).
 */
export const TourExitConfirmModal: React.FC = () => {
  const { t } = useTranslation();
  const { exitPromptPending, resumeAfterExitPrompt, confirmPauseFromExitPrompt, confirmDismissFromExitPrompt } = useTour();

  return (
    <Modal
      isOpen={exitPromptPending}
      onClose={resumeAfterExitPrompt}
      backdropClassName="fixed inset-0 z-[1100] p-4 sm:p-6 bg-black/60 backdrop-blur-sm overflow-y-auto custom-scrollbar"
    >
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="tour-exit-prompt-title"
        aria-describedby="tour-exit-prompt-body"
        className="w-full max-w-sm bg-white dark:bg-slate-900 rounded-[1.75rem] shadow-2xl border border-slate-100 dark:border-slate-800 p-6 space-y-5 animate-fadeIn"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div>
          <h3 id="tour-exit-prompt-title" className="text-base font-black text-slate-800 dark:text-slate-100 mb-1.5">
            {t('home.onboarding.exitPrompt.title')}
          </h3>
          <p id="tour-exit-prompt-body" className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
            {t('home.onboarding.exitPrompt.body')}
          </p>
        </div>
        <div className="flex flex-col gap-2">
          <button
            onClick={resumeAfterExitPrompt}
            className="flex items-center justify-center gap-2 px-4 py-3 bg-brand-600 text-white rounded-xl font-black text-[11px] uppercase tracking-widest hover:bg-[#00509d] transition-all shadow-lg shadow-blue-900/20 cursor-pointer"
          >
            <ArrowLeftCircle size={14} /> {t('home.onboarding.exitPrompt.continue')}
          </button>
          <button
            onClick={confirmPauseFromExitPrompt}
            className="label-field flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 transition-all cursor-pointer"
          >
            <PauseCircle size={14} /> {t('home.onboarding.exitPrompt.pause')}
          </button>
          <button
            onClick={confirmDismissFromExitPrompt}
            className="label-field flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl hover:text-rose-500 transition-all cursor-pointer"
          >
            <LogOut size={14} /> {t('home.onboarding.exitPrompt.dismiss')}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default TourExitConfirmModal;
